/**
 * Three-way theme: "system" (follows the OS / Apple appearance), "dark", or
 * "light". The choice is persisted; `system` tracks `prefers-color-scheme` live.
 */
export type Theme = "system" | "dark" | "light";

const KEY = "zc-theme";

export function getThemePref(): Theme {
  const v = localStorage.getItem(KEY);
  return v === "dark" || v === "light" || v === "system" ? v : "system";
}

function systemDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;
}

/** Resolve + apply the effective theme to <html> (data-theme = dark|light). */
export function applyTheme(pref: Theme = getThemePref()): void {
  const root = document.documentElement;
  root.dataset.theme =
    pref === "system" ? (systemDark() ? "dark" : "light") : pref;
  root.dataset.themePref = pref;
}

export function setThemePref(pref: Theme): void {
  localStorage.setItem(KEY, pref);
  applyTheme(pref);
}

/** Cycle system → light → dark → system. Returns the new preference. */
export function cycleTheme(): Theme {
  const order: Theme[] = ["system", "light", "dark"];
  const next = order[(order.indexOf(getThemePref()) + 1) % order.length]!;
  setThemePref(next);
  return next;
}

export function themeLabel(pref: Theme = getThemePref()): string {
  return pref === "system" ? "Auto" : pref === "dark" ? "Dark" : "Light";
}

/** Emoji glyph for the current preference: monitor=Auto, sun=Light, moon=Dark. */
export function themeIcon(pref: Theme = getThemePref()): string {
  return pref === "system" ? "🖥️" : pref === "dark" ? "🌙" : "☀️";
}

export function initTheme(): void {
  applyTheme();
  window
    .matchMedia?.("(prefers-color-scheme: dark)")
    .addEventListener?.("change", () => {
      if (getThemePref() === "system") applyTheme("system");
    });
}
